/**
 * Seed packets, as the remodelled landing page (landing.V2) has it: the
 * gardener's worry that typing every plant in takes forever, the same
 * heading and words as before (plus the third line, "See what you can plant
 * right now", landing.packets.plantNow*), and the evidence as a story in three steps:
 * the photo of the pile with a camera's viewfinder corners, a slow green scan
 * line and a "One photo" tag; the line "Grow reads every packet"; and the
 * "Add 6 plants" card with three of the packets.
 *
 * The scan line slides down and up the photo; for a visitor with reduced
 * motion it holds still across the middle (motion-reduce).
 *
 * The photographs are the owner's own scan of their bed "Side garden"
 * (LANDING_PACKET_PHOTOS in config/tunables/landing).
 *
 * Words (en/es): landing.v2.packets.*, and the unchanged landing.packets.*,
 * landing.slots.*.
 *
 * @module SeedPacketsV2
 */

import React from 'react';
import { Camera, Package } from 'lucide-react';
import { LANDING_PACKET_PHOTOS } from '@/config/tunables/landing';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { FeatureBlock } from '../components/FeatureBlock';
import { FlowArrow } from '../components/FlowArrow';
import { PhotoSlot } from '../components/PhotoSlot';
import { splitAround } from '../lib/splitAround';

/** One example plant: its name and dates. */
interface Example {
  /** The row's key, and its slot in LANDING_PACKET_PHOTOS. */
  id: 'packet1' | 'packet2' | 'packet3';
  /** The plant's name (a translation key). */
  name: string;
  /** Its dates (a translation key). */
  dates: string;
}

/** The packets from the example photo that the list shows (three of its six). */
const EXAMPLES: readonly Example[] = [
  { id: 'packet1', name: 'landing.packets.items.packet1.name', dates: 'landing.packets.items.packet1.dates' },
  { id: 'packet2', name: 'landing.packets.items.packet2.name', dates: 'landing.packets.items.packet2.dates' },
  { id: 'packet3', name: 'landing.packets.items.packet3.name', dates: 'landing.packets.items.packet3.dates' },
];

/** The shared look of a camera-viewfinder corner; each corner adds its own position and open sides. */
const CORNER = 'absolute h-[34px] w-[34px] border-4 border-white drop-shadow-[0_1px_3px_rgb(0_0_0/0.5)]';

/** The seed packets section. */
export const SeedPacketsV2: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  // "forever" is emphasised inside the gardener's sentence, so the translation keeps the sentence whole.
  const [before, after] = splitAround(t, 'landing.v2.packets.pain', 'forever');
  return (
    <FeatureBlock
      icon={<Package className="h-8 w-8" strokeWidth={1.75} />}
      quote
      pain={<>{before}<em>{t('landing.v2.packets.painEm')}</em>{after}</>}
      title={t('landing.packets.title')}
      line={<b>{t('landing.packets.lead')}</b>}
      second={t('landing.packets.body')}
      third={<><b>{t('landing.packets.plantNowBold')}</b>{t('landing.packets.plantNowRest')}</>}
      evidence={(
        <>
          <div className="relative">
            <PhotoSlot label={t('landing.slots.pile')} className="aspect-[4/5] w-full rounded-2xl object-center shadow-xl" src={LANDING_PACKET_PHOTOS.pile} alwaysShown />
            {/* The viewfinder: corners, the scan line and the tag are drawn over the photo. */}
            <span aria-hidden="true" className={`${CORNER} left-3.5 top-3.5 rounded-tl-[10px] border-b-0 border-r-0`} />
            <span aria-hidden="true" className={`${CORNER} right-3.5 top-3.5 rounded-tr-[10px] border-b-0 border-l-0`} />
            <span aria-hidden="true" className={`${CORNER} bottom-3.5 left-3.5 rounded-bl-[10px] border-r-0 border-t-0`} />
            <span aria-hidden="true" className={`${CORNER} bottom-3.5 right-3.5 rounded-br-[10px] border-l-0 border-t-0`} />
            <span
              aria-hidden="true"
              className="absolute inset-x-[22px] top-[22px] h-[3px] animate-landing-scan rounded-[3px] bg-[#A4E09A] shadow-[0_0_14px_4px_rgb(164_224_154/0.7)] motion-reduce:top-1/2 motion-reduce:animate-none"
            />
            <span className="absolute bottom-[26px] left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-[rgb(28_26_23/0.78)] px-3 py-1.5 font-sans text-[13px] font-semibold leading-none text-white">
              <Camera className="h-4 w-4" aria-hidden="true" />
              {t('landing.v2.packets.scanTag')}
            </span>
          </div>
          <FlowArrow>{t('landing.v2.packets.reads')}</FlowArrow>
          <div className="space-y-3 rounded-2xl border-2 border-primary bg-card p-4 text-left shadow-xl">
            <p className="font-grow text-base font-bold">{t('landing.packets.listTitle')}</p>
            {EXAMPLES.map((example) => (
              <div key={example.id} className="flex items-center gap-3">
                <PhotoSlot label={t(`landing.slots.${example.id}`)} className="h-16 w-12 shrink-0 p-1 text-[10px] leading-tight" src={LANDING_PACKET_PHOTOS[example.id]} alwaysShown />
                <p>
                  <b>{t(example.name)}</b>
                  <br />
                  {t(example.dates)}
                </p>
              </div>
            ))}
            <p className="text-sm text-muted-foreground">{t('landing.packets.more')}</p>
          </div>
        </>
      )}
    />
  );
};
